"use client";

import { type ReactNode, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  type Connect,
  PlaneSvg,
} from "@/components/tools/worksheets-math/plane-svg";
import {
  ActionBar,
  defaultSettings,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import { pointLetter } from "@/lib/tool-kit/worksheets/numberline";
import {
  formatPair,
  generatePoints,
  PLANE_MAXIMA,
  type PlaneSpec,
  type Point,
  parsePairs,
  type Quadrants,
} from "@/lib/tool-kit/worksheets/plane";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const COORDINATE_PLANE_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Coordinate Plane",
  quadrantsLabel: "Plane",
  quadrants: { one: "One quadrant", four: "Four quadrants" },
  maxLabel: "Axes run to",
  labelEveryLabel: "Number every",
  labelEveryAuto: "Automatic",
  xNameLabel: "Horizontal axis name",
  yNameLabel: "Vertical axis name",
  numberAxes: "Number the axes",
  quadrantLabels: "Mark the quadrants I, II, III, IV",
  taskLabel: "Worksheet type",
  tasks: {
    blank: "Blank planes",
    plot: "Plot the points",
    name: "Name the points",
  },
  sourceLabel: "Points",
  sources: { random: "Random points", typed: "My own points" },
  pointCountLabel: "Points per plane",
  typedLabel: "Ordered pairs, like (2, 3) (−4, 1)",
  typedPlaceholder: "(2, 3) (−4, 1) (0, 5)",
  allowAxes: "Allow points on the axes",
  spread: "Take points from each quadrant in turn",
  connectLabel: "Connect the points",
  connects: {
    none: "Do not connect",
    open: "Connect in order",
    closed: "Connect in order and close the shape",
  },
  planesLabel: "Planes on the page",
  rejected: "Not used, because they are unreadable or off the plane: {items}",
  plotPrompt: "Plot and label these points:",
  namePrompt: "Write the coordinates of each point:",
  planeLabel: "Coordinate plane with {quadrants}, axes from {min} to {max}",
  pairLine: "{letter} {pair}",
} as const;

type Task = keyof typeof COORDINATE_PLANE_GENERATOR_STRINGS.tasks;
type Source = keyof typeof COORDINATE_PLANE_GENERATOR_STRINGS.sources;

const LABEL_EVERY = [0, 1, 2, 5, 10];
const COUNTS = [3, 4, 5, 6, 8, 10, 12];
const PLANE_COUNTS = [1, 2, 4];

export default function CoordinatePlaneGeneratorWidget() {
  const strings = useToolUi(COORDINATE_PLANE_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ header: true, answerKey: true, count: 1 })
  );
  const seed = useWorksheetSeed();
  const [quadrants, setQuadrants] = useState<Quadrants>("four");
  const [max, setMax] = useState(10);
  const [labelEvery, setLabelEvery] = useState(0);
  const [xName, setXName] = useState("x");
  const [yName, setYName] = useState("y");
  const [numbered, setNumbered] = useState(true);
  const [roman, setRoman] = useState(false);
  const [task, setTask] = useState<Task>("plot");
  const [source, setSource] = useState<Source>("random");
  const [count, setCount] = useState(5);
  const [typed, setTyped] = useState("");
  const [allowAxes, setAllowAxes] = useState(false);
  const [spread, setSpread] = useState(true);
  const [connect, setConnect] = useState<Connect>("none");

  const spec: PlaneSpec = { quadrants, max };
  const planes = settings.count;
  const typedPairs = parsePairs(typed, spec);
  const usesSeed = task !== "blank" && source === "random";
  const rng = createRng(seed.seed);
  const pointSets: Point[][] = Array.from({ length: planes }, () => {
    if (task === "blank") {
      return [];
    }
    return source === "typed"
      ? typedPairs.items
      : generatePoints(spec, { count, allowAxes, spread }, rng);
  });
  const hasKey = task !== "blank";
  const label = t(strings.planeLabel, {
    quadrants: strings.quadrants[quadrants],
    min: quadrants === "one" ? "0" : `−${max}`,
    max: String(max),
  });

  const plane = (points: readonly Point[], letters: boolean) => (
    <PlaneSvg
      connect={connect}
      label={label}
      labelEvery={labelEvery}
      letters={letters}
      numbered={numbered}
      points={points}
      quadrantLabels={roman}
      spec={spec}
      xName={xName}
      yName={yName}
    />
  );
  const pairs = (points: readonly Point[]) =>
    points.map((p, index) =>
      t(strings.pairLine, { letter: pointLetter(index), pair: formatPair(p) })
    );
  const cells = (render: (points: Point[]) => ReactNode) => (
    <div
      className="grid gap-x-6 gap-y-6"
      style={{
        gridTemplateColumns: `repeat(${planes === 1 ? 1 : 2}, minmax(0, 1fr))`,
      }}
    >
      {pointSets.map((points, index) => (
        <div
          className="ws-avoid-break mx-auto w-full"
          // biome-ignore lint/suspicious/noArrayIndexKey: planes are positional
          key={index}
          style={planes === 1 ? { maxWidth: "6.2in" } : undefined}
        >
          {render(points)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.quadrantsLabel}
            name="cp-quadrants"
            onChange={setQuadrants}
            options={(["one", "four"] as const).map((value) => ({
              value,
              label: strings.quadrants[value],
            }))}
            value={quadrants}
          />
          <PillGroup
            label={strings.taskLabel}
            name="cp-task"
            onChange={setTask}
            options={(["blank", "plot", "name"] as const).map((value) => ({
              value,
              label: strings.tasks[value],
            }))}
            value={task}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="cp-max"
            label={strings.maxLabel}
            onChange={(value) => setMax(Number(value))}
            options={PLANE_MAXIMA.map((value) => ({
              value: String(value),
              label: String(value),
            }))}
            value={String(max)}
          />
          <SelectField
            id="cp-label-every"
            label={strings.labelEveryLabel}
            onChange={(value) => setLabelEvery(Number(value))}
            options={LABEL_EVERY.map((value) => ({
              value: String(value),
              label: value === 0 ? strings.labelEveryAuto : String(value),
            }))}
            value={String(labelEvery)}
          />
          <TextField
            id="cp-x-name"
            label={strings.xNameLabel}
            onChange={setXName}
            value={xName}
          />
          <TextField
            id="cp-y-name"
            label={strings.yNameLabel}
            onChange={setYName}
            value={yName}
          />
        </div>
        {task === "blank" ? null : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SelectField
                id="cp-source"
                label={strings.sourceLabel}
                onChange={(value: Source) => setSource(value)}
                options={(["random", "typed"] as const).map((value) => ({
                  value,
                  label: strings.sources[value],
                }))}
                value={source}
              />
              {source === "random" ? (
                <SelectField
                  id="cp-count"
                  label={strings.pointCountLabel}
                  onChange={(value) => setCount(Number(value))}
                  options={COUNTS.map((value) => ({
                    value: String(value),
                    label: String(value),
                  }))}
                  value={String(count)}
                />
              ) : null}
              <SelectField
                id="cp-connect"
                label={strings.connectLabel}
                onChange={(value: Connect) => setConnect(value)}
                options={(["none", "open", "closed"] as const).map((value) => ({
                  value,
                  label: strings.connects[value],
                }))}
                value={connect}
              />
            </div>
            {source === "typed" ? (
              <TextAreaField
                id="cp-typed"
                label={strings.typedLabel}
                onChange={setTyped}
                placeholder={strings.typedPlaceholder}
                rows={3}
                value={typed}
              />
            ) : (
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <Checkbox
                  checked={allowAxes}
                  label={strings.allowAxes}
                  onChange={setAllowAxes}
                />
                {quadrants === "four" ? (
                  <Checkbox
                    checked={spread}
                    label={strings.spread}
                    onChange={setSpread}
                  />
                ) : null}
              </div>
            )}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="cp-planes"
            label={strings.planesLabel}
            onChange={(value) => update({ count: Number(value) })}
            options={PLANE_COUNTS.map((value) => ({
              value: String(value),
              label: String(value),
            }))}
            value={String(planes)}
          />
          <SelectField
            id="cp-paper"
            label={strings.paperLabel}
            onChange={(paper) => update({ paper })}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={settings.paper}
          />
          <TextField
            hint={strings.titleHint}
            id="cp-title"
            label={strings.titleLabel}
            onChange={(title) => update({ title })}
            value={settings.title}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={numbered}
            label={strings.numberAxes}
            onChange={setNumbered}
          />
          {quadrants === "four" ? (
            <Checkbox
              checked={roman}
              label={strings.quadrantLabels}
              onChange={setRoman}
            />
          ) : null}
          <Checkbox
            checked={settings.header}
            label={strings.showHeader}
            onChange={(header) => update({ header })}
          />
          {hasKey ? (
            <Checkbox
              checked={settings.answerKey}
              label={strings.answerKey}
              onChange={(answerKey) => update({ answerKey })}
            />
          ) : null}
        </div>
        {source === "typed" &&
        task !== "blank" &&
        typedPairs.rejected.length > 0 ? (
          <Notice>
            {t(strings.rejected, { items: typedPairs.rejected.join("; ") })}
          </Notice>
        ) : null}
        <ActionBar
          idPrefix="cp"
          seed={seed}
          showSeed={usesSeed}
          strings={strings}
        />
      </div>
      <WorksheetSheets
        answers={cells((points) => (
          <>
            {plane(points, true)}
            <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 font-mono text-sm">
              {pairs(points).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </>
        ))}
        problems={cells((points) => (
          <>
            {plane(task === "name" ? points : [], task === "name")}
            {task === "plot" ? (
              <div className="mt-2 text-sm">
                <p className="font-medium">{strings.plotPrompt}</p>
                <ul className="flex flex-wrap gap-x-4 gap-y-1 font-mono">
                  {pairs(points).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {task === "name" ? (
              <div className="mt-2 text-sm">
                <p className="font-medium">{strings.namePrompt}</p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-2 font-mono">
                  {points.map((_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: points are positional
                    <li key={index}>
                      {pointLetter(index)} (
                      <span className="inline-block min-w-[2.5ch] border-neutral-900 border-b">
                        &nbsp;
                      </span>
                      ,{" "}
                      <span className="inline-block min-w-[2.5ch] border-neutral-900 border-b">
                        &nbsp;
                      </span>
                      )
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ))}
        seed={seed.seed}
        settings={{ ...settings, answerKey: settings.answerKey && hasKey }}
        showSeed={usesSeed}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
      />
    </div>
  );
}
